import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { LegalLayout, Section } from './LegalLayout';
import { api } from '@/lib/api';

interface HealthSnapshot {
  api: 'operational' | 'degraded' | 'down' | 'unknown';
  ai: 'operational' | 'degraded' | 'down' | 'unknown';
  vector: 'operational' | 'degraded' | 'down' | 'unknown';
  ingest: 'operational' | 'degraded' | 'down' | 'unknown';
  checkedAt: string;
}

/**
 * Live status surface. Three components: API, AI routing,
 * Vector store. We probe the public /health endpoint; if anything is
 * red the corresponding badge flips. No login required.
 *
 * The status feed itself is presented honestly — if there are no public
 * incidents, the page says so. We never invent an "all systems green"
 * banner that we can't substantiate.
 */
export function StatusPage() {
  const { t } = useTranslation();

  const { data, isLoading } = useQuery<HealthSnapshot>({
    queryKey: ['public-health'],
    queryFn: async () => {
      try {
        const r = await api.get<HealthSnapshot>('/health');
        return r.data;
      } catch {
        // Health endpoint unreachable — assume API down, AI / vector unknown.
        return {
          api: 'down',
          ai: 'unknown',
          vector: 'unknown',
          ingest: 'unknown',
          checkedAt: new Date().toISOString(),
        };
      }
    },
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  return (
    <LegalLayout
      eyebrow={t('status.eyebrow', { defaultValue: 'Status' }) as string}
      title={t('status.title', { defaultValue: 'System status' })}
      updated={data?.checkedAt ?? new Date().toISOString()}
    >
      <Section id="now" kicker="now" title={t('status.now.title', { defaultValue: 'Right now' }) as string}>
        {isLoading ? (
          <p className="text-ink-500">{t('common.loading', { defaultValue: 'Loading…' })}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <Component
              label={t('status.comp.api', { defaultValue: 'API' }) as string}
              level={data?.api ?? 'unknown'}
              t={t}
            />
            <Component
              label={t('status.comp.ai', { defaultValue: 'AI routing' }) as string}
              level={data?.ai ?? 'unknown'}
              t={t}
            />
            <Component
              label={t('status.comp.vector', { defaultValue: 'Vector store' }) as string}
              level={data?.vector ?? 'unknown'}
              t={t}
            />
            <Component
              label={t('status.comp.ingest', { defaultValue: 'Ingest pipeline' }) as string}
              level={data?.ingest ?? 'unknown'}
              t={t}
            />
          </div>
        )}
        <p className="mt-4 text-[12px] text-ink-500 font-mono">
          {t('status.refresh', { defaultValue: 'Auto-refreshes every 30 seconds' })}
        </p>
      </Section>

      <Section id="incidents" kicker="history" title={t('status.history.title', { defaultValue: 'Recent incidents' }) as string}>
        <p>
          {t('status.history.empty', {
            defaultValue:
              'No public incidents to report in the past 90 days. When an incident is open, an entry appears here with a one-line summary, the affected components, and a permalink to the post-mortem once published.',
          })}
        </p>
      </Section>

      <Section id="subscribe" kicker="alerts" title={t('status.alerts.title', { defaultValue: 'Get notified' }) as string}>
        <p>
          {t('status.alerts.body', {
            defaultValue:
              'During an incident we update knowlez.com/status first, then notify workspace owners by email and (where configured) Slack / Teams / LINE WORKS / Chatwork. The in-product notification bell also shows the active incident banner.',
          })}
        </p>
      </Section>

      <Section id="planned" kicker="upcoming" title={t('status.planned.title', { defaultValue: 'Planned maintenance' }) as string}>
        <p>
          {t('status.planned.body', {
            defaultValue:
              'Maintenance windows are announced at least 7 days in advance. Read-paths stay online during all routine maintenance; write-paths may briefly pause while migrations run.',
          })}
        </p>
      </Section>
    </LegalLayout>
  );
}

function Component({
  label,
  level,
  t,
}: {
  label: string;
  level: 'operational' | 'degraded' | 'down' | 'unknown';
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  const styles: Record<typeof level, string> = {
    operational: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    degraded: 'border-citation-200 bg-citation-50 text-citation-800',
    down: 'border-warn-500/30 bg-warn-500/10 text-warn-600',
    unknown: 'border-ink-200 bg-ink-50 text-ink-500',
  };
  const dot: Record<typeof level, string> = {
    operational: 'bg-emerald-500',
    degraded: 'bg-citation-500',
    down: 'bg-warn-500',
    unknown: 'bg-ink-300',
  };
  return (
    <div className={`rounded-lg border px-4 py-3 flex items-center justify-between ${styles[level]}`}>
      <span className="text-[13px] font-medium">{label}</span>
      <span className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] font-semibold">
        <span className={`inline-block h-2 w-2 rounded-full ${dot[level]}`} />
        {t(`status.level.${level}`, { defaultValue: level })}
      </span>
    </div>
  );
}
